import React, { useState, useRef, useCallback, useEffect, memo, useMemo } from 'react';
import type { FC } from 'react';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { N0, N40, N200 } from '@atlaskit/theme/colors';
import { prefersReducedMotion } from '@atlaskit/motion';
import { token } from '@atlaskit/tokens';

import { ProfileAvatar } from '@confluence/profile';
import { getEmojiValueFromContent } from '@confluence/emoji-title/entry-points/transformer';
import { useElementSeenCallback } from '@confluence/analytics';
import { useBooleanFeatureFlag } from '@confluence/session-data';

import {
	getContentLink,
	getSpaceLink,
	getUserAccountId,
	getLinkForSummaryLinkUpdate,
	isSupportedEvent,
	fadeInAnimation,
} from '../feedCard-helpers';
import { AllActivityDropdown, ActivityLineUpdateLink } from '../ActivityLine';
import { ActionTime } from '../ActionTime';
import { useFeedCardAnalyticsEvents } from '../useFeedCardAnalyticsEvents';
import type {
	FeedContentQuery_feed_nodes as FeedNodeType,
	FeedContentQuery_feed_nodes_summaryLineUpdate_FeedEventComment as SummaryLineCommentUpdate,
} from '../../__types__/FeedContentQuery';
import { FeedEventType, FeedItemSourceType } from '../../__types__/FeedContentQuery';
import { useIsMounted } from '../useIsMounted';
import type { FollowingFeedSortByValue } from '../../Feed';

import { FeedCardContext } from './FeedCardContext';
import { FeedCardBody } from './FeedCardBody';
import { FeedCardInvisibleFullSizedLink } from './FeedCardInvisibleFullSizedLink';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const SimpleWrapper = styled.div`
	display: flex;
	flex-direction: row;
	background: ${token('elevation.surface.raised', N0)};
	border: 1px solid ${token('color.border', N40)};
	borderradius: ${token('border.radius.200', '8px')};
	padding: ${token('space.200', '16px')};
	margin-bottom: ${token('space.150', '12px')};
	& > div {
		width: 100%;
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const Wrapper = styled.div<{
	isActionMenuOpen: boolean;
	disableAnimation?: boolean;
}>`
	position: relative;
	display: flex;
	flex-direction: row;
	background: ${token('elevation.surface.raised', N0)};
	border: 1px solid ${token('color.border', N40)};
	border-radius: ${token('border.radius.200', '8px')};
	&:hover {
		box-shadow: ${token(
			'elevation.shadow.raised',
			'0px 1px 1px rgba(9, 30, 66, 0.25), 0px 0px 1px rgba(9, 30, 66, 0.31)',
		)};
	}
	padding: ${token('space.200', '16px')};
	margin-bottom: ${token('space.150', '12px')};
	--show-action: ${({ isActionMenuOpen }) => (isActionMenuOpen ? 1 : 0)};
	&:hover,
	&:active,
	&:focus-within {
		cursor: pointer;
		--show-action: 1;
	}
	${prefersReducedMotion()};
	${({ disableAnimation }) => !disableAnimation && fadeInAnimation}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const AvatarWrapper = styled.div`
	padding-right: ${token('space.200', '16px')};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const SummaryLine = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: ${token('space.150', '12px')};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const SummaryLineRelevantAction = styled.div`
	display: flex;
	flex-direction: row;
	color: ${token('color.text.subtle', N200)};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	align-items: center;

	flex-grow: 1;

	// handle the active state outline with overflow: hidden
	padding: ${token('space.050', '4px')};
	margin: ${token('space.negative.050', '-4px')};
	margin-right: ${token('space.050', '4px')}; // padding + margin should be 8px
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const SummaryLineActions = styled.div`
	color: ${token('color.text.subtle', N200)};
	display: flex;
	align-items: center;
	flex-shrink: 0;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const Spacer = styled.div`
	margin: 0 ${token('space.050', '4px')};
`;

type FeedCardProps = {
	feedItem: FeedNodeType;
	ranking: number;
	disableAnimation?: boolean;
	shouldHideComments?: boolean;
	followingFeedSortby?: FollowingFeedSortByValue;
};

export const FeedCard: FC<FeedCardProps> = memo(
	({ feedItem, ranking, disableAnimation, shouldHideComments, followingFeedSortby }) => {
		const isSpaceAliasFFEnabled = useBooleanFeatureFlag('confluence.frontend.space.alias');

		const isMounted = useIsMounted();
		const [hasBeenHovered, setHasBeenHovered] = useState(false);
		const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
		const bottomOfCardRef = useRef<HTMLDivElement | null>(null);

		const mainUpdate = feedItem.summaryLineUpdate;
		const recentActionsCount = feedItem.recentActionsCount;
		const mainUpdateUserId = getUserAccountId(mainUpdate.user);
		const updateType = mainUpdate?.type;
		const contentUrl = getContentLink(feedItem.content);
		const updateTime = mainUpdate?.datetime;
		const contentId = feedItem?.content?.id || '';
		const updateLink = getLinkForSummaryLinkUpdate(mainUpdate, contentUrl, contentId);
		const contentType = feedItem?.content?.type || 'page';
		const contentSubType = feedItem?.content?.subType;
		const contentTitle = feedItem?.content?.title || '';
		const contentExcerpt = feedItem.content.excerpt;
		const authorName = feedItem?.content?.history?.createdBy?.displayName || '';
		const ownerName = feedItem?.content?.history?.ownedBy?.displayName || '';
		const spaceId = feedItem?.content?.space?.id || '';
		const spaceKey = feedItem?.content?.space?.key || undefined;
		const spaceName = feedItem?.content?.space?.name || '';
		const lastModifiedDate = feedItem.content?.metadata?.lastModifiedDate || '';
		const emoji = getEmojiValueFromContent(feedItem.content);
		const numComments = feedItem?.content?.metadata?.comments?.commentsCount || 0;
		const spaceUrl = getSpaceLink(feedItem.content, isSpaceAliasFFEnabled);
		const authorId = getUserAccountId(feedItem.content.history?.createdBy) || '';
		const ownerId = getUserAccountId(feedItem.content.history?.ownedBy) || '';
		const hasNullComment =
			updateType === FeedEventType.COMMENT &&
			(mainUpdate as SummaryLineCommentUpdate).comment === null;
		const supportedEvent = isSupportedEvent(feedItem.summaryLineUpdate.type);

		const sourceIds = feedItem.source.reduce<string[]>((filtered, source) => {
			if (source === FeedItemSourceType.PERSON && mainUpdateUserId) {
				filtered.push(mainUpdateUserId);
			} else if (source === FeedItemSourceType.SPACE && spaceId) {
				filtered.push(spaceId);
			}
			return filtered;
		}, []);

		const handleMouseEnter = React.useCallback(() => {
			setHasBeenHovered(true);
		}, [setHasBeenHovered]);

		const fireAnalyticsEvent = useFeedCardAnalyticsEvents(contentId, spaceId, 'following', {
			ranking,
			contentType,
			updateType,
			sourceIds,
			isFromSource: feedItem.source,
			recentActionsCount,
			commentsCount: numComments,
			lastUpdated: new Date(mainUpdate.datetime).getTime(),
		});

		const onObserved = useCallback(() => {
			fireAnalyticsEvent({
				type: 'sendTrackEvent',
				action: 'observed',
				actionSubject: 'feedCard',
			});
		}, [fireAnalyticsEvent]);

		useElementSeenCallback(bottomOfCardRef, onObserved);

		useEffect(() => {
			if (hasNullComment) {
				fireAnalyticsEvent({
					type: 'sendOperationalEvent',
					action: 'hidden',
					actionSubject: 'nullCommentFeedCard',
				});
			}
		}, [hasNullComment, fireAnalyticsEvent]);

		const feedCardContextValue = useMemo(() => ({ fireAnalyticsEvent }), [fireAnalyticsEvent]);

		if (hasNullComment || !supportedEvent) {
			return null;
		}

		const cardHref =
			shouldHideComments && updateType === FeedEventType.COMMENT ? updateLink : contentUrl;

		return (
			<FeedCardContext.Provider value={feedCardContextValue}>
				<Wrapper
					data-testid="feed-card"
					isActionMenuOpen={isActionMenuOpen}
					disableAnimation={isMounted || disableAnimation}
					onMouseEnter={handleMouseEnter}
					onFocus={handleMouseEnter}
				>
					<FeedCardInvisibleFullSizedLink
						href={cardHref}
						contentType={contentType}
						followingFeedSortby={followingFeedSortby}
					/>
					<AvatarWrapper>
						<ProfileAvatar
							userId={mainUpdateUserId}
							src={mainUpdate.user?.profilePicture?.path || ''}
							canViewProfile
							size="medium"
							name={mainUpdate.user?.displayName ?? ''}
						/>
					</AvatarWrapper>
					<ContentWrapper>
						<SummaryLine>
							<SummaryLineRelevantAction>
								<ActivityLineUpdateLink
									type={updateType}
									userDisplayName={mainUpdate.user.displayName}
									link={updateLink}
									groupSize={mainUpdate.groupSize}
									activityDropdownLineItem={false}
								/>
								<Spacer>•</Spacer>
								<ActionTime actionTime={updateTime} />
							</SummaryLineRelevantAction>

							<SummaryLineActions>
								{recentActionsCount > 1 && (
									<AllActivityDropdown
										contentUrl={contentUrl}
										contentId={contentId}
										recentActionsCount={recentActionsCount}
									/>
								)}
							</SummaryLineActions>
						</SummaryLine>
						<FeedCardBody
							toggleActionMenu={setIsActionMenuOpen}
							contentId={contentId}
							emoji={emoji}
							contentTitle={contentTitle}
							contentType={contentType}
							contentSubType={contentSubType}
							contentUrl={contentUrl}
							spaceId={spaceId}
							spaceKey={spaceKey}
							spaceName={spaceName}
							spaceUrl={spaceUrl}
							authorName={authorName}
							authorId={authorId}
							ownerName={ownerName}
							ownerId={ownerId}
							contentExcerpt={contentExcerpt}
							numComments={numComments}
							lastModifiedDate={lastModifiedDate}
							comment={
								updateType === FeedEventType.COMMENT
									? (mainUpdate as SummaryLineCommentUpdate)
									: undefined
							}
							hasBeenHovered={hasBeenHovered}
							source="following-feed"
							shouldHideComments={shouldHideComments}
							followingFeedSortby={followingFeedSortby}
						/>
						<div ref={bottomOfCardRef} />
					</ContentWrapper>
				</Wrapper>
			</FeedCardContext.Provider>
		);
	},
);
